import { makeObservable, observable, action } from "mobx";
import { OrganizationModel, type IOrganizationModel } from "models/company/entities/OrganizationModel";


import { CustomerModel, ICustomerModel } from "models/user/customer/entities/CustomerModel";
import OrganizationAPI from "services/OrganizationAPI";



/**
 * Store para gerenciar o estado relacionado a Organizações e seus Clientes.
 */
class OrganizationStore {
    // --- Estado Observável ---
    selectedCustomer?: CustomerModel;
    selectedOrganization?: OrganizationModel;
    organization?: OrganizationModel;
    organizations: OrganizationModel[] = [];
    finalNodeOrganizations: OrganizationModel[] = [];
    customers: CustomerModel[] = [];
    loading: boolean = false;

    constructor() {
        // Configura a reatividade da store com MobX
        makeObservable(this, {
            selectedCustomer: observable,
            selectedOrganization: observable,
            organization: observable,
            organizations: observable,
            finalNodeOrganizations: observable,
            customers: observable,
            loading: observable,
            reset: action,
            emptyOrganization: action,
            selectCustomer: action,
            selectOrganization: action,
            setLoading: action,
            setOrganization: action,
            get: action,
            searchFinalNodeByName: action,
            getOrganizationCustomers: action,
        });
    }

    // --- Ações (Métodos que modificam o Estado) ---

    /** Reseta o estado da store para os valores iniciais. */
    reset(): void {
        this.selectedCustomer = undefined;
        this.selectedOrganization = undefined;
        this.organization = undefined;
        this.organizations = [];
        this.customers = [];
        this.finalNodeOrganizations = [];
        this.setLoading(false);
    }

    /** Cria uma nova instância de organização vazia. */
    emptyOrganization(): void {
        this.organization = undefined;
    }

    /** Define o cliente selecionado. */
    selectCustomer = (customer: CustomerModel): void => {
        this.selectedCustomer = customer;
    }

    /** Define a organização selecionada. */
    selectOrganization(organization: OrganizationModel): void {
        this.selectedOrganization = organization;
    }

    /** Define o estado de carregamento. */
    setLoading(value: boolean): void {
        this.loading = value;
    }

    /** Define a organização principal. */
    setOrganization(organization: OrganizationModel): void {
        this.organization = organization;
    }

    /** Busca uma organização pelo seu UUID. */
    async get(uuid: string): Promise<void> {
        this.setLoading(true);
        try {
            const response = await OrganizationAPI.get(uuid);
            if (!response.error) {
                this.setOrganization(new OrganizationModel(response));
            } else {
                console.error("Erro ao buscar organização:", response.error);
            }
        } catch (error) {
            console.error("Falha na chamada API para get:", error);
        } finally {
            this.setLoading(false);
        }
    }


    /** Busca uma organização "folha" pelo nome dentro de uma organização pai. */
    async searchFinalNodeByName(uuid: string, name: string): Promise<OrganizationModel | undefined> {
        this.setLoading(true);
        try {
            const response = await OrganizationAPI.searchFinalNodeByName(uuid, name);
            if (!response.error) {
                const org = new OrganizationModel(response);
                this.setOrganization(org);
                return org;
            } else {
                console.error("Erro ao buscar final node por nome:", response.error);
            }
        } catch (error) {
            console.error("Falha na chamada API para searchFinalNodeByName:", error);
        } finally {
            this.setLoading(false);
        }
        return undefined;
    }

    /** Busca os clientes de uma organização específica. */
    async getOrganizationCustomers(uuid: string): Promise<CustomerModel[] | undefined> {
        this.setLoading(true);
        try {
            const response = await OrganizationAPI.getOrganizationCustomers(uuid, {
                ordering: "full_name",
            });
            if (!response.error) {
                const userList = response.map((item: ICustomerModel) => new CustomerModel(item));
                this.customers = userList;
                return userList;
            } else {
                console.error("Erro ao buscar clientes da organização:", response.error);
            }
        } catch (error) {
            console.error("Falha na chamada API para getOrganizationCustomers:", error);
        } finally {
            this.setLoading(false);
        }
        return undefined;
    }
}

export default OrganizationStore;